import { useEffect, useId, useRef, useState } from 'react';
import { Checkbox } from '@rocco/components/checkbox';
import { cn } from '@rocco/utils/cn';
import type { BuildHubOption } from '@ui-rocco/hooks/use-build-hubs';
import type { BuildHubFieldSelection } from '~/modules/enquiry/hooks/use-build-hub-field-selection';
import { GATE_FIELD_LABEL_CLASS } from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { EnquiryProjectPicker } from './EnquiryProjectPicker';
import { ProjectThumbnail } from './ProjectThumbnail';
import { shareChipsFor, sharedSubtitle } from './project-summary';

export interface EnquiryProjectDetailsCardProps {
    /** The selected project's id, as held by the form's `buildHubId` field. */
    value?: string;
    onChange: (value: string | undefined) => void;
    onBlur?: () => void;
    disabled?: boolean;
    buildHubs?: BuildHubOption[];
    isLoading: boolean;
    /** Refetch the list once a project has been created into it. */
    onRefetchBuildHubs?: () => void;
    fieldSelection: BuildHubFieldSelection;
    onFieldSelectionChange: (selection: BuildHubFieldSelection) => void;
    /** Named in the copy so the reader knows exactly who receives the detail. */
    professionalTitle: string;
    className?: string;
}

/**
 * Sharing the reader's saved project with the professional, as an opt-in.
 *
 * The enquiry can carry the project's budget, stage, scope, location and dates
 * so the professional answers with real detail rather than replying to a cold
 * message. That is worth asking for plainly — a checkbox the reader ticks —
 * and worth being specific about, so the chips name the values that will be
 * sent and each one can be dropped without dropping the project.
 *
 * Ticked, it attaches the most recently active project immediately rather than
 * asking which one first: the common case is one project, or the one just
 * worked on. Which project, and which of its facets, are both a step further
 * in — reachable, but never in the way of sending.
 */
export const EnquiryProjectDetailsCard = ({
    value,
    onChange,
    onBlur,
    disabled = false,
    buildHubs,
    isLoading,
    onRefetchBuildHubs,
    fieldSelection,
    onFieldSelectionChange,
    professionalTitle,
    className,
}: EnquiryProjectDetailsCardProps) => {
    /**
     * The tick answers to the reader alone, so it flips on the click rather
     * than waiting for the form value to come back round. Deriving it from
     * `value` made unticking feel broken: the box stayed checked for as long
     * as the field took to clear.
     *
     * A project arriving from anywhere else — a restored draft — still means
     * opted in, but only while the reader has not just said otherwise.
     */
    const [optedIn, setOptedIn] = useState(!!value);
    const untickedRef = useRef(false);
    const [sharingOpen, setSharingOpen] = useState(false);
    const [choosing, setChoosing] = useState(false);
    const included = optedIn;

    useEffect(() => {
        if (value && !untickedRef.current) setOptedIn(true);
    }, [value]);
    const checkboxId = useId();
    const regionId = useId();
    const sharingId = useId();

    const projects = buildHubs ?? [];
    const projectsLoaded = !isLoading && !!buildHubs;
    const selectedProject = value
        ? projects.find((project) => String(project.ID) === String(value))
        : undefined;
    /**
     * A project just created here is attached before the list it belongs to
     * has been refetched, so it is legitimately absent for a beat. Without
     * this the orphan rule below detaches it — and the empty-attach rule then
     * binds a different project entirely.
     */
    const justCreatedRef = useRef<string | undefined>(undefined);
    if (value && String(value) === String(justCreatedRef.current)) {
        if (selectedProject) justCreatedRef.current = undefined;
    }
    const awaitingCreated =
        !!value && String(value) === String(justCreatedRef.current);
    /** Attached to a project this account can no longer see. */
    const isOrphaned =
        !!value && projectsLoaded && !selectedProject && !awaitingCreated;

    // Detach rather than send it anyway. The chips are what the reader agreed
    // to share, and an unresolvable project renders none of them — the enquiry
    // would carry a title, region, scope and dates they were never shown.
    //
    // Unticking with it: left ticked, the empty-attach rule immediately binds
    // whichever project is first, so a draft naming one project would send
    // another one's details.
    useEffect(() => {
        if (!isOrphaned) return;
        setOptedIn(false);
        untickedRef.current = true;
        onChange(undefined);
    }, [isOrphaned, onChange]);

    // Ticked with nothing attached: take the first, which the server returns
    // most-recently-active first. One project means there is nothing to
    // choose, so the reader is never asked.
    useEffect(() => {
        if (!optedIn || value || !projectsLoaded) return;
        const first = buildHubs?.[0];
        if (first) onChange(first.ID);
    }, [optedIn, value, projectsLoaded, buildHubs, onChange]);

    const handleToggle = (next: boolean) => {
        untickedRef.current = !next;
        setOptedIn(next);
        if (next) return;
        setSharingOpen(false);
        setChoosing(false);
        // Unticking must detach the project, not merely hide the controls.
        if (value) onChange(undefined);
    };

    const handleSelect = (id: string, created = false) => {
        if (created) justCreatedRef.current = id;
        onChange(id);
        onBlur?.();
        setChoosing(false);
    };

    const toggleChip = (field: keyof BuildHubFieldSelection) =>
        onFieldSelectionChange({
            ...fieldSelection,
            [field]: !fieldSelection[field],
        });

    const chips = selectedProject ? shareChipsFor(selectedProject) : [];
    const chosenCount = chips.filter(
        (chip) => fieldSelection[chip.field],
    ).length;
    /**
     * Always reachable. The picker is also the only way to create a project,
     * so gating it on there being a second one left an account with one — or
     * none — unable to add another.
     */
    const canChange = true;

    return (
        <div className={cn('flex flex-col gap-3', className)}>
            <div className="flex items-start gap-3">
                <Checkbox
                    id={checkboxId}
                    checked={included}
                    disabled={disabled}
                    aria-expanded={included}
                    aria-controls={regionId}
                    onCheckedChange={(next) => handleToggle(next === true)}
                    className="mt-0.5 shrink-0"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <label
                            htmlFor={checkboxId}
                            className="cursor-pointer text-[0.875rem] font-medium tracking-[-0.02em] text-[#161616]"
                        >
                            {`Include my project details`}
                        </label>
                        <span className="rounded-[0.1875rem] bg-black/5 px-1.5 py-0.5 text-[0.5625rem] font-medium tracking-[0.07em] text-[#656565] uppercase">
                            {`New`}
                        </span>
                    </div>
                    <p className="text-[0.8125rem] leading-snug text-[#828282]">
                        {`Give ${professionalTitle} context from your saved project so they can answer properly instead of replying to a cold enquiry.`}
                    </p>
                </div>
            </div>

            {included && (
                <div id={regionId} className="flex flex-col gap-3">
                    {choosing ? (
                        <EnquiryProjectPicker
                            projects={projects}
                            selectedId={value}
                            disabled={disabled}
                            onSelect={handleSelect}
                            onCreated={(id) => handleSelect(id, true)}
                            onRefetch={onRefetchBuildHubs}
                        />
                    ) : selectedProject ? (
                        <div className="flex items-center gap-3 rounded-[0.625rem] bg-[#f6f6f6] p-3">
                            <ProjectThumbnail
                                project={selectedProject}
                                size={48}
                            />
                            <div className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate text-[0.875rem] font-medium text-[#161616]">
                                    {selectedProject.Title}
                                </span>
                                {!!sharedSubtitle(
                                    selectedProject,
                                    fieldSelection,
                                ) && (
                                    <span className="truncate text-[0.8125rem] text-[#828282]">
                                        {sharedSubtitle(
                                            selectedProject,
                                            fieldSelection,
                                        )}
                                    </span>
                                )}
                            </div>
                            {canChange && (
                                <button
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => setChoosing(true)}
                                    className="h-8 shrink-0 rounded-[var(--radius-btn-xs)] border border-[#9d9d9d] px-3 text-[0.75rem] font-medium text-[#656565] disabled:opacity-50"
                                >
                                    {`Change`}
                                </button>
                            )}
                        </div>
                    ) : projectsLoaded ? (
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={() => setChoosing(true)}
                            className="self-start text-[0.8125rem] font-medium text-[#161616] underline disabled:opacity-50"
                        >
                            {`Add a project`}
                        </button>
                    ) : (
                        <p className="text-[0.8125rem] text-[#828282]">
                            {`Loading your projects…`}
                        </p>
                    )}

                    {!!selectedProject && (
                        <>
                            {/* Names what always goes: the selection hook locks
                                `includeTitle` and `includeLocation` on, so a
                                bare "only what you choose" would be a promise
                                the payload does not keep. */}
                            <p className="text-[0.75rem] text-[#828282]">
                                {`Sharing sends your project name and region, plus what you choose. `}
                                <button
                                    type="button"
                                    disabled={disabled}
                                    aria-expanded={sharingOpen}
                                    aria-controls={sharingId}
                                    onClick={() => setSharingOpen(!sharingOpen)}
                                    className="font-medium text-[#161616] underline disabled:opacity-50"
                                >
                                    {sharingOpen ? `Hide` : `What's shared?`}
                                </button>
                            </p>

                            {sharingOpen && chips.length > 0 && (
                                <div
                                    id={sharingId}
                                    className="flex flex-col gap-2"
                                >
                                    <div className="flex items-baseline justify-between gap-3">
                                        <span
                                            className={GATE_FIELD_LABEL_CLASS}
                                        >
                                            {`Share with this enquiry`}
                                        </span>
                                        <span className="text-[0.75rem] text-[#828282]">
                                            {`${chosenCount} of ${chips.length}`}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {chips.map((chip) => {
                                            const on =
                                                fieldSelection[chip.field];
                                            return (
                                                <button
                                                    key={chip.field}
                                                    type="button"
                                                    disabled={disabled}
                                                    aria-pressed={on}
                                                    onClick={() =>
                                                        toggleChip(chip.field)
                                                    }
                                                    className={cn(
                                                        'rounded-[var(--radius-btn-xs)] border px-3 py-1.5 text-[0.8125rem] tracking-[-0.02em] disabled:opacity-50',
                                                        on
                                                            ? 'border-[#181818] bg-[#181818] text-[#f3f3f3]'
                                                            : 'border-black/15 bg-white text-[#656565]',
                                                    )}
                                                >
                                                    <span aria-hidden>
                                                        {on ? '✓ ' : '+ '}
                                                    </span>
                                                    {chip.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[0.75rem] text-[#828282]">
                                        {`${professionalTitle} sees only the selected fields — never your other projects.`}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
